import React from "react";
import { useParams, useLocation } from "react-router";
import { useIsMutating } from "@tanstack/react-query";

// Misc
import * as types from "types";
import styles from "styles/chat.module.scss";
import { isLocalOrSandbox } from "utilities/environment";
import httpErrorStatus from "utilities/httpErrorStatus";
// From contexts
import { useUploadProgress, uploadFiles } from "contexts/UploadContext";
import type { UploadProgress } from "contexts/UploadContext";
// From components
import WelcomeMessage from "components/WelcomeMessage/WelcomeMessage";
import PersonaSelector from "components/PersonaSelector";
import IncognitoToggle from "components/IncognitoToggle";
import MessageInput from "components/MessageInput";
import ScrollToBottomButton from "components/ScrollToBottomButton";
import UploadDropzone from "components/UploadDropzone";
import UploadList from "components/UploadList";
import MessageHistory from "components/MessageHistory";
import useGetAccessToken from "components/Login/useGetAccessToken";
import ErrorBoundary from "components/ErrorBoundary";
import MessagesSkeleton from "./components/MessagesSkeleton";
import MessagesReadError from "./components/MessagesReadError";
import ChatNotFound from "./components/ChatNotFound";
// From services
import useChatStream from "services/cortevaChain/hooks/useChatStream";
import { PUT_MESSAGES_MUTATION_KEY } from "services/dbApi/hooks/usePutMessagesMutation";
import useAutomaticChatTitle from "services/cortevaChain/hooks/useAutomaticChatTitle";
import useThreadsQuery from "services/dbApi/hooks/useThreadsQuery";
// From hooks
import useDocumentTitle from "hooks/useDocumentTitle";
import { useCurrentThread } from "./hooks/useCurrentThread";
import { useCurrentPersona } from "./hooks/useCurrentPersona";
import { useCurrentMessages } from "./hooks/useCurrentMessages";
import { useStickToBottom } from "hooks/useStickToBottom";
import { useChatTasks } from "./hooks/useChatTasks";

interface ActiveChatProps {
  threadId: string;
}

function ActiveChat({ threadId }: ActiveChatProps) {
  const [thread, setThread, commitThread] = useCurrentThread(threadId);
  const [messages, setMessages, commitMessages, refetchMessages] =
    useCurrentMessages(threadId);
  const [persona, setPersona] = useCurrentPersona(threadId);

  const getAccessToken = useGetAccessToken();
  const { setUploadProgress, removeUploadProgress, uploads } =
    useUploadProgress();

  const location = useLocation();

  const chatWindowRef = React.useRef<null | HTMLDivElement>(null);
  // Follow/pause auto-scroll for streaming responses.
  const { isPinned, scrollToBottom, contentRef } = useStickToBottom({
    containerRef: chatWindowRef,
    messages,
    activeChatId: threadId,
  });

  // Local state, seeded from router state when navigating here after a file
  // upload in NewChat, so the typed draft is not lost on route transition.
  const [userInput, setUserInput] = React.useState(() => {
    const navDraftInput = location?.state?.draftInput;
    return navDraftInput !== undefined ? navDraftInput : "";
  });

  // Persist the final message list when the stream ends.
  const handleStreamEnd = React.useCallback(
    (finalMessages: types.Message[]) => {
      commitMessages(finalMessages);
    },
    [commitMessages]
  );

  // Restore the aborted user message into the draft. useChatStream strips the
  // user message and any partial assistant response on abort.
  const handleStreamAbort = React.useCallback((userMessageContent: string) => {
    setUserInput(userMessageContent);
  }, []);

  const streamState = useChatStream({
    chatThreadId: threadId,
    persona: persona,
    messages: messages,
    setMessages: setMessages,
    getAccessToken: getAccessToken,
    onAbort: handleStreamAbort,
    onStreamEnd: handleStreamEnd,
  });

  // Everything this chat needs for long-running tasks: see useChatTasks.
  const { onAskAboutTask, refetchTasksUnlessWriting } = useChatTasks({
    threadId,
    mcpConfig: persona.mcpConfig,
    getAccessToken,
    messages,
    setMessages,
    commitMessages,
    streamState,
  });

  // Stream title tokens into the threads cache; commit on stream end to persist.
  const setTitle = React.useCallback(
    (newTitle: string) => {
      setThread((prev) => ({ ...prev, name: newTitle }));
    },
    [setThread]
  );

  const handleTitleStreamEnd = React.useCallback(
    (finalTitle: string) => {
      commitThread((prev) => ({ ...prev, name: finalTitle }));
    },
    [commitThread]
  );

  const titleIsStreaming = useAutomaticChatTitle({
    chatThreadId: threadId,
    personaId: 14,
    model: "gpt-4.1-mini",
    messages: messages,
    enabled: streamState.status !== "loading",
    title: thread.name,
    setTitle,
    getAccessToken: getAccessToken,
    onStreamEnd: handleTitleStreamEnd,
  });

  const { refetch: refetchThreads } = useThreadsQuery();

  useDocumentTitle(thread.name);

  // Derived state used in context gauge.
  const lastAssistantWithUsage = messages.findLast(
    (m): m is types.AssistantResponseMessage =>
      types.isAssistantResponseMessage(m) && m.tokenUsage !== undefined
  );
  const lastTokenUsage = lastAssistantWithUsage?.tokenUsage;
  const compactionFailed = lastAssistantWithUsage?.compactionFailed ?? false;

  interface HandleUserMessageOpts {
    message: types.UserMessage;
  }
  const handleUserMessage = React.useCallback(
    async ({ message }: HandleUserMessageOpts) => {
      if (streamState.status === "loading") return;

      setMessages((prevMessages) => [...prevMessages, message]);
    },
    [streamState.status, setMessages]
  );

  interface HandleFileUploadsOpts {
    files: File[];
  }
  const handleFileUploads = React.useCallback(
    async ({ files }: HandleFileUploadsOpts) => {
      if (files.length === 0) return;
      if (streamState.status === "loading") return;

      const fileMessages = await uploadFiles({
        files: files,
        chatId: threadId,
        getAccessToken,
        setUploadProgress,
        removeUploadProgress,
      });

      // Optimistic append; stale state survives until the next messages refetch if the PUT fails.
      commitMessages((prev) => [...prev, ...fileMessages]);
    },
    [
      commitMessages,
      getAccessToken,
      removeUploadProgress,
      setUploadProgress,
      streamState.status,
      threadId,
    ]
  );

  // Refetch on tab focus to bring in changes from other tabs. Skipped while a
  // stream is in flight (would clobber the partial response), while a title is
  // streaming, or while the messages PUT is in flight (a GET could land with
  // pre-commit server state and overwrite a just-committed message).
  const putInFlight = useIsMutating({
    mutationKey: PUT_MESSAGES_MUTATION_KEY,
  });
  React.useEffect(() => {
    const handleFocus = () => {
      if (streamState.status === "loading") return;
      if (titleIsStreaming) return;
      if (putInFlight > 0) return;
      refetchMessages();
      refetchThreads();
      refetchTasksUnlessWriting();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [
    streamState.status,
    refetchMessages,
    refetchThreads,
    refetchTasksUnlessWriting,
    putInFlight,
    titleIsStreaming,
  ]);

  const uploadList = Object.values(uploads[threadId] ?? {});
  const isUploading = uploadList.some(
    (upload: UploadProgress) => upload.status === "uploading"
  );
  const uploadsDisabled = streamState.status === "loading" || isUploading;
  const messageInputDisabled = streamState.status === "loading" || isUploading;

  let body = <WelcomeMessage persona={persona} />;
  if (messages.length > 0) {
    body = (
      <MessageHistory
        chatThread={thread}
        contentRef={contentRef}
        messages={messages}
        isStreaming={streamState.status === "loading"}
        getAccessToken={getAccessToken}
        onAskAboutTask={onAskAboutTask}
      />
    );
  }

  const searchEnabled = !persona.isInternal;

  const jupyterHubEnabled =
    isLocalOrSandbox() &&
    (persona.jupyterHubCapable === undefined ||
      persona.jupyterHubCapable === true);
  return (
    <UploadDropzone
      disabled={uploadsDisabled}
      onDrop={(files) => handleFileUploads({ files })}
    >
      <main className={styles.chat}>
        {/* Header */}
        <div className={styles.chatHeader}>
          <PersonaSelector
            personas={types.getActivePersonas()}
            selectedPersona={persona}
            setSelectedPersona={setPersona}
          />
          <IncognitoToggle />
        </div>
        {/* Body */}
        <div className={styles.chatBody} ref={chatWindowRef}>
          {body}
        </div>
        {!isPinned && messages.length > 0 && (
          <ScrollToBottomButton onClick={scrollToBottom} />
        )}

        {/* Sticky footer */}
        <div className={styles.chatFooter}>
          <UploadList activeChatId={threadId} className="mb3" />
          <MessageInput
            persona={persona}
            disabled={messageInputDisabled}
            uploadDisabled={uploadsDisabled}
            onStop={streamState.abort}
            userInput={userInput}
            setUserInput={setUserInput}
            onSend={(message) => handleUserMessage({ message })}
            onPasteFiles={(files) => handleFileUploads({ files })}
            onFileDrop={(files) => handleFileUploads({ files })}
            lastTokenUsage={lastTokenUsage}
            compactionFailed={compactionFailed}
            toolAvailability={{
              searchEnabled,
              jupyterHubEnabled,
            }}
          />
        </div>
      </main>
    </UploadDropzone>
  );
}

// Two not-found signals reach this boundary: a 404 from the messages endpoint
// (on `error.response`) and a "Thread not found:" Error from `useCurrentThread`.
function isThreadNotFound(error: Error): boolean {
  if (error.message.startsWith("Thread not found:")) return true;
  if ("response" in error) {
    const response = error.response;
    if (response instanceof Response && response.status === 404) return true;
  }
  return false;
}

function isRecoverableError(error: Error): boolean {
  const status = httpErrorStatus(error);
  if (status === undefined) return false;
  return status === 401 || status >= 500;
}

export default function Chat() {
  const params = useParams();
  const threadId = params.chatId;
  if (threadId === undefined)
    throw new Error("Chat route rendered without a threadId");

  // key={threadId} resets the boundary on navigation so a 404 on chat A
  // doesn't block chat B.
  return (
    <ErrorBoundary
      key={threadId}
      fallback={(error, reset) => {
        if (isThreadNotFound(error)) {
          return <ChatNotFound threadId={threadId} />;
        }
        if (isRecoverableError(error)) {
          return (
            <MessagesReadError
              threadId={threadId}
              error={error}
              onReset={reset}
            />
          );
        }
        throw error;
      }}
    >
      <React.Suspense fallback={<MessagesSkeleton />}>
        <ActiveChat threadId={threadId} />
      </React.Suspense>
    </ErrorBoundary>
  );
}
